/**
 * Pagina /superadmin/configuracion-cuota — Sprint 13 S13-22.
 *
 * Mantenedor del super_admin (y tesorero) para registrar el monto de
 * cuota semestral del barrio. Tabla append-only: cada cambio se
 * agrega como fila nueva con vigente_desde posterior, NO se edita el
 * monto vigente.
 *
 * Sin esta configuracion cargada, el flujo de generacion de cuotas
 * del semestre falla con 'No hay configuracion vigente'. Es prerequisito
 * para LIVE inicial.
 *
 * RBAC ruta: super_admin (sa.calbucura.cl). Backend acepta tambien
 * tesorero, pero esta UI vive solo en sa.
 *
 * Sprint 15 S15-01 (2026-05-20): seccion "Datos de cuenta bancaria del
 * barrio" con 6 campos opcionales. Singleton implicito: la fila vigente
 * tiene la cuenta operativa actual. Al rotar la tesoreria se crea una
 * fila nueva con `vigente_desde` futuro. Los datos aparecen en el modal
 * "Datos para transferir" del portal vecino + footer del email de
 * cron morosidad cuando estan los 6 poblados.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarConfiguracionCuota,
  crearConfiguracionCuota,
  listConfiguracionesCuota,
  type ConfiguracionCuotaRead,
  type TipoCuentaBancaria,
} from '../../lib/api/configuracion_cuota';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { InputFecha } from '../../components/ui/InputFecha';
import { formatCLP, formatRut } from '../../lib/format';
import { hoy } from '../../lib/tiempo';

const LABEL_TIPO_CUENTA: Record<TipoCuentaBancaria, string> = {
  corriente: 'Cuenta Corriente',
  vista: 'Cuenta Vista',
  ahorro: 'Cuenta de Ahorro',
};

// Sprint 15 S15-01: state mutable de los 6 campos de cuenta bancaria,
// con sus setters, para compartir entre los modales Crear y Editar.
interface CuentaBancariaState {
  titularNombre: string;
  titularRut: string;
  banco: string;
  tipo: TipoCuentaBancaria | '';
  numero: string;
  correo: string;
}

const CUENTA_VACIA: CuentaBancariaState = {
  titularNombre: '',
  titularRut: '',
  banco: '',
  tipo: '',
  numero: '',
  correo: '',
};

interface CuentaBancariaFieldsProps {
  state: CuentaBancariaState;
  onChange: (next: CuentaBancariaState) => void;
}

function CuentaBancariaFieldsSection({ state, onChange }: CuentaBancariaFieldsProps) {
  function setField<K extends keyof CuentaBancariaState>(
    key: K,
    value: CuentaBancariaState[K],
  ) {
    onChange({ ...state, [key]: value });
  }
  return (
    <fieldset className="space-y-3 pt-2 border-t border-white/10">
      <legend className="text-sm font-medium text-text-primary pt-2">
        Datos de cuenta bancaria del barrio
      </legend>
      <p className="text-xs text-text-secondary -mt-2">
        Opcionales. Si los rellenás, deben estar los <strong>6</strong> para
        que se muestren en el portal vecino (modal "Datos para transferir") y
        en el pie del email de cron morosidad. El RUT se valida con dígito
        verificador (módulo 11).
      </p>

      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">Titular — nombre completo</span>
        <input
          type="text"
          value={state.titularNombre}
          onChange={(e) => setField('titularNombre', e.target.value)}
          placeholder="Juan Pérez A."
          maxLength={120}
          className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
        />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Titular — RUT</span>
          <input
            type="text"
            value={state.titularRut}
            onChange={(e) => setField('titularRut', e.target.value)}
            placeholder="12.345.678-9"
            maxLength={15}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Banco</span>
          <input
            type="text"
            value={state.banco}
            onChange={(e) => setField('banco', e.target.value)}
            placeholder="BancoEstado"
            maxLength={40}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Tipo de cuenta</span>
          <select
            value={state.tipo}
            onChange={(e) => setField('tipo', e.target.value as TipoCuentaBancaria | '')}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          >
            <option value="">— Elegir —</option>
            <option value="corriente">Cuenta Corriente</option>
            <option value="vista">Cuenta Vista</option>
            <option value="ahorro">Cuenta de Ahorro</option>
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Número de cuenta</span>
          <input
            type="text"
            value={state.numero}
            onChange={(e) => setField('numero', e.target.value)}
            placeholder="000-0000-000"
            maxLength={40}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>
      </div>

      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">
          Correo destino del comprobante{' '}
          <span className="text-state-warning text-xs">
            (crítico para auto-distribución)
          </span>
        </span>
        <input
          type="email"
          value={state.correo}
          onChange={(e) => setField('correo', e.target.value)}
          placeholder="correo@correo.com"
          maxLength={255}
          className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
        />
        <span className="text-xs text-text-muted block">
          Los vecinos configuran este correo como destinatario en su banca
          móvil; el banco le envía el comprobante automáticamente y el parser
          lo procesa.
        </span>
      </label>
    </fieldset>
  );
}

// Helper: del state local a payload nullable (string vacío → null).
function cuentaStateToPayload(s: CuentaBancariaState) {
  return {
    cuenta_banco_titular_nombre: s.titularNombre.trim() || null,
    cuenta_banco_titular_rut: s.titularRut.trim() || null,
    cuenta_banco_nombre: s.banco.trim() || null,
    cuenta_banco_tipo: (s.tipo || null) as TipoCuentaBancaria | null,
    cuenta_banco_numero: s.numero.trim() || null,
    cuenta_correo_destino: s.correo.trim() || null,
  };
}

// Helper: hidratar state desde una ConfiguracionCuotaRead existente.
function cuentaStateFromRead(c: ConfiguracionCuotaRead): CuentaBancariaState {
  return {
    titularNombre: c.cuenta_banco_titular_nombre ?? '',
    titularRut: c.cuenta_banco_titular_rut ?? '',
    banco: c.cuenta_banco_nombre ?? '',
    tipo: c.cuenta_banco_tipo ?? '',
    numero: c.cuenta_banco_numero ?? '',
    correo: c.cuenta_correo_destino ?? '',
  };
}

// ============================================================
// Régimen de morosidad (convención #20, 2026-10-06)
// ============================================================

interface RegimenMorosidadState {
  plazo: string;
  moroso: string;
  suspendible: string;
  desde: string;
}

const REGIMEN_DEFAULT: RegimenMorosidadState = {
  plazo: '1',
  moroso: '2',
  suspendible: '3',
  desde: '2027-01-01',
};

function regimenFromRead(c: ConfiguracionCuotaRead): RegimenMorosidadState {
  return {
    plazo: String(c.meses_plazo_pago),
    moroso: String(c.meses_atraso_moroso),
    suspendible: String(c.meses_atraso_suspendible),
    desde: c.regimen_plazo_desde,
  };
}

function regimenToPayload(r: RegimenMorosidadState) {
  return {
    meses_plazo_pago: parseInt(r.plazo, 10),
    meses_atraso_moroso: parseInt(r.moroso, 10),
    meses_atraso_suspendible: parseInt(r.suspendible, 10),
    regimen_plazo_desde: r.desde,
  };
}

function regimenValido(r: RegimenMorosidadState): boolean {
  const plazo = parseInt(r.plazo, 10);
  const moroso = parseInt(r.moroso, 10);
  const suspendible = parseInt(r.suspendible, 10);
  return (
    plazo >= 1 && plazo <= 6 && moroso >= 2 && suspendible > moroso && r.desde !== ''
  );
}

/** Plazo de pago y umbrales de nivel. Ver docs/dev/doctrina-morosidad-vecino.md. */
function RegimenMorosidadFieldsSection({
  state,
  onChange,
}: {
  state: RegimenMorosidadState;
  onChange: (s: RegimenMorosidadState) => void;
}) {
  const campo = (
    clave: 'plazo' | 'moroso' | 'suspendible',
    etiqueta: string,
    min: number,
    max?: number,
  ) => (
    <label className="text-sm space-y-1 block">
      <span className="text-text-secondary">{etiqueta}</span>
      <input
        type="number"
        value={state[clave]}
        onChange={(e) => onChange({ ...state, [clave]: e.target.value })}
        min={min}
        max={max}
        className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
      />
    </label>
  );
  return (
    <fieldset className="border-[0.5px] border-white/15 rounded p-3 space-y-3">
      <legend className="text-xs uppercase tracking-wide text-text-muted px-1">
        Régimen de morosidad
      </legend>
      <p className="text-xs text-text-muted">
        La cuota se paga dentro de los primeros meses del semestre. Pasado el
        plazo, cada mes sin pagar sube de nivel: deudor (1 mes), moroso y
        suspendible (habilitada para suspender).
      </p>
      {campo('plazo', 'Meses de plazo para pagar (1 = hasta el 31-01 / 31-07)', 1, 6)}
      <div className="grid grid-cols-2 gap-3">
        {campo('moroso', 'Moroso desde (meses de atraso)', 2)}
        {campo('suspendible', 'Suspendible desde (meses de atraso)', 3)}
      </div>
      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">
          Rige por plazo para cuotas desde
        </span>
        <InputFecha
          value={state.desde}
          onChange={(v) => onChange({ ...state, desde: v })}
          className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          requerido
        />
        <span className="text-xs text-text-muted block">
          Las cuotas anteriores se miden por la cuenta mensual (transición).
        </span>
      </label>
      {!regimenValido(state) && (
        <p className="text-xs text-state-danger" role="alert">
          El plazo va de 1 a 6 meses, moroso desde 2 o más, y suspendible
          tiene que ser mayor que moroso.
        </p>
      )}
    </fieldset>
  );
}

interface ModalCrearProps {
  onCerrar: () => void;
  onCreada: () => void;
}

function ModalCrear({ onCerrar, onCreada }: ModalCrearProps) {
  const [montoSemestral, setMontoSemestral] = useState('');
  const [montoMensual, setMontoMensual] = useState('');
  const [vigenteDesde, setVigenteDesde] = useState('');
  const [maxControles, setMaxControles] = useState('4');
  const [actaUrl, setActaUrl] = useState('');
  const [regimen, setRegimen] = useState<RegimenMorosidadState>(REGIMEN_DEFAULT);
  const [cuenta, setCuenta] = useState<CuentaBancariaState>(CUENTA_VACIA);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-calcular mensual cuando el usuario sale del semestral, si esta vacio.
  function handleSemestralBlur() {
    if (montoSemestral && !montoMensual) {
      const semestral = parseInt(montoSemestral, 10);
      if (!Number.isNaN(semestral) && semestral > 0) {
        setMontoMensual(String(Math.round(semestral / 6)));
      }
    }
  }

  const valido =
    montoSemestral !== '' &&
    montoMensual !== '' &&
    vigenteDesde !== '' &&
    parseInt(montoSemestral, 10) > 0 &&
    parseInt(montoMensual, 10) > 0 &&
    regimenValido(regimen);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await crearConfiguracionCuota({
        monto_semestral: parseInt(montoSemestral, 10),
        monto_mensual_equivalente: parseInt(montoMensual, 10),
        vigente_desde: vigenteDesde,
        max_controles_por_lote: parseInt(maxControles, 10) || 4,
        aprobado_acta_url: actaUrl.trim() || null,
        ...regimenToPayload(regimen),
        ...cuentaStateToPayload(cuenta),
      });
      onCreada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Nueva configuración de cuota"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Nueva configuración de cuota
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-3 text-xs text-text-secondary">
          La cuota semestral es el monto operativo; el mensual equivalente es
          solo informativo (UI: "equivale a $X/mes"). Esta tabla es
          <span className="font-medium text-text-primary"> append-only</span>:
          cada cambio se agrega como una nueva fila con su propia fecha de
          vigencia.
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Monto semestral (CLP) <span className="text-state-danger">*</span>
          </span>
          <input
            type="number"
            value={montoSemestral}
            onChange={(e) => setMontoSemestral(e.target.value)}
            onBlur={handleSemestralBlur}
            placeholder="180000"
            min={1}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Monto mensual equivalente (CLP){' '}
            <span className="text-state-danger">*</span>
          </span>
          <input
            type="number"
            value={montoMensual}
            onChange={(e) => setMontoMensual(e.target.value)}
            placeholder="30000"
            min={1}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
          <span className="text-xs text-text-muted block">
            Se auto-calcula como semestral/6 al perder foco si está vacío.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Vigente desde <span className="text-state-danger">*</span>
          </span>
          <InputFecha
            value={vigenteDesde}
            onChange={setVigenteDesde}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            requerido
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Máximo de controles remotos por casa
          </span>
          <input
            type="number"
            value={maxControles}
            onChange={(e) => setMaxControles(e.target.value)}
            min={1}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
          <span className="text-xs text-text-muted block">
            Tope de CR activos por casa (default 4). La comisión puede
            aprobar excepciones manualmente.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">URL del acta (opcional)</span>
          <input
            type="url"
            value={actaUrl}
            onChange={(e) => setActaUrl(e.target.value)}
            placeholder="https://..."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <RegimenMorosidadFieldsSection state={regimen} onChange={setRegimen} />

        <CuentaBancariaFieldsSection state={cuenta} onChange={setCuenta} />

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : 'Guardar configuración'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface ModalEditarProps {
  configuracion: ConfiguracionCuotaRead;
  onCerrar: () => void;
  onActualizada: () => void;
}

function ModalEditar({ configuracion, onCerrar, onActualizada }: ModalEditarProps) {
  const [actaUrl, setActaUrl] = useState(configuracion.aprobado_acta_url ?? '');
  const [maxControles, setMaxControles] = useState(
    String(configuracion.max_controles_por_lote ?? 4),
  );
  const [regimen, setRegimen] = useState<RegimenMorosidadState>(
    regimenFromRead(configuracion),
  );
  const [cuenta, setCuenta] = useState<CuentaBancariaState>(
    cuentaStateFromRead(configuracion),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !regimenValido(regimen)) return;
    setSubmitting(true);
    setError(null);
    try {
      await actualizarConfiguracionCuota(configuracion.id, {
        aprobado_acta_url: actaUrl.trim() || null,
        max_controles_por_lote: parseInt(maxControles, 10) || 4,
        ...regimenToPayload(regimen),
        ...cuentaStateToPayload(cuenta),
      });
      onActualizada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Editar configuración de cuota"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Editar configuración
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-3 text-xs text-text-secondary">
          Se permite editar el <strong>URL del acta</strong> y los{' '}
          <strong>datos de cuenta bancaria</strong> (correcciones puntuales,
          typos). Para reflejar un nuevo monto o una rotación completa de
          tesorería (cuenta distinta), crear una configuración nueva con
          vigente_desde posterior.
        </div>

        <div className="text-sm text-text-secondary space-y-0.5">
          <div>
            Monto semestral:{' '}
            <span className="text-text-primary font-medium">
              {formatCLP(configuracion.monto_semestral)}
            </span>
          </div>
          <div>
            Equivalente mensual:{' '}
            <span className="text-text-primary font-medium">
              {formatCLP(configuracion.monto_mensual_equivalente)}
            </span>
          </div>
          <div>
            Vigente desde:{' '}
            <span className="text-text-primary font-medium">
              {configuracion.vigente_desde}
            </span>
          </div>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Máximo de controles remotos por casa
          </span>
          <input
            type="number"
            value={maxControles}
            onChange={(e) => setMaxControles(e.target.value)}
            min={1}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
          <span className="text-xs text-text-muted block">
            Tope de CR activos por casa (default 4). La comisión puede
            aprobar excepciones manualmente.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">URL del acta</span>
          <input
            type="url"
            value={actaUrl}
            onChange={(e) => setActaUrl(e.target.value)}
            placeholder="https://..."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <RegimenMorosidadFieldsSection state={regimen} onChange={setRegimen} />

        <CuentaBancariaFieldsSection state={cuenta} onChange={setCuenta} />

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function ConfiguracionCuotaPage() {
  const [items, setItems] = useState<ConfiguracionCuotaRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [editando, setEditando] = useState<ConfiguracionCuotaRead | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listConfiguracionesCuota()
      .then((data) => {
        if (cancelado) return;
        setItems(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar configuraciones`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  // La vigente al dia de fechaHoy: la mas reciente con vigente_desde <= today.
  const vigente = useMemo(() => {
    const fechaHoy = hoy();
    return items.find((c) => c.vigente_desde <= fechaHoy) ?? null;
  }, [items]);

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Configuración financiera"
        title="Configuración de cuota"
        description="Monto semestral del barrio. La tabla es append-only: cada cambio aprobado por acta se registra como una fila nueva con su fecha de vigencia. Sin esto cargado no se pueden generar cuotas del semestre."
        action={
          <Button variant="primary" onClick={() => setMostrarCrear(true)}>
            + Nueva configuración
          </Button>
        }
      />

      {vigente && (
        <div className="bg-state-success/10 border-l-4 border-state-success rounded-sm p-4 max-w-3xl space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-state-success font-medium">
              Vigente fechaHoy
            </div>
            <div className="text-text-primary mt-1">
              <span className="text-2xl font-medium">
                {formatCLP(vigente.monto_semestral)}
              </span>{' '}
              <span className="text-sm text-text-secondary">por semestre</span>
            </div>
            <div className="text-sm text-text-secondary mt-1">
              Equivale a {formatCLP(vigente.monto_mensual_equivalente)} mensuales
              · vigente desde {vigente.vigente_desde}
            </div>
          </div>

          {/* Sprint 15 S15-01: estado de la cuenta bancaria del barrio. */}
          {(() => {
            const cuentaCompleta =
              vigente.cuenta_banco_titular_nombre &&
              vigente.cuenta_banco_titular_rut &&
              vigente.cuenta_banco_nombre &&
              vigente.cuenta_banco_tipo &&
              vigente.cuenta_banco_numero &&
              vigente.cuenta_correo_destino;
            if (cuentaCompleta) {
              return (
                <div className="border-t border-state-success/30 pt-3">
                  <div className="text-xs uppercase tracking-wide text-state-success font-medium mb-1">
                    Cuenta bancaria activa (vecinos ven estos datos)
                  </div>
                  <div className="text-sm text-text-primary grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                    <div>
                      <span className="text-text-secondary">Banco:</span>{' '}
                      {vigente.cuenta_banco_nombre}
                    </div>
                    <div>
                      <span className="text-text-secondary">Tipo:</span>{' '}
                      {LABEL_TIPO_CUENTA[vigente.cuenta_banco_tipo!]}
                    </div>
                    <div>
                      <span className="text-text-secondary">N° cuenta:</span>{' '}
                      <span className="font-mono">
                        {vigente.cuenta_banco_numero}
                      </span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Titular:</span>{' '}
                      {vigente.cuenta_banco_titular_nombre}
                    </div>
                    <div>
                      <span className="text-text-secondary">RUT:</span>{' '}
                      <span className="font-mono">
                        {formatRut(vigente.cuenta_banco_titular_rut)}
                      </span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Correo:</span>{' '}
                      <span className="font-mono">
                        {vigente.cuenta_correo_destino}
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="border-t border-state-warning/30 pt-3 text-sm text-state-warning">
                ⚠ Cuenta bancaria <strong>incompleta</strong> — el modal "Datos
                para transferir" del portal vecino no se mostrará y el footer
                del email de morosidad queda sin bloque de pago. Edita esta
                configuración y completa los 6 campos para activar el flujo.
              </div>
            );
          })()}
        </div>
      )}

      {!loading && !vigente && items.length === 0 && (
        <div className="bg-state-danger/10 border-l-4 border-state-danger rounded-sm p-4 max-w-3xl">
          <div className="text-sm text-text-primary font-medium">
            ⚠ No hay configuración de cuota cargada
          </div>
          <p className="text-sm text-text-secondary mt-1">
            Cargá la configuración inicial con el monto semestral del barrio
            antes de generar cuotas o invitar a los vecinos al portal.
          </p>
        </div>
      )}

      {!loading && !vigente && items.length > 0 && (
        <div className="bg-state-warning/10 border-l-4 border-state-warning rounded-sm p-4 max-w-3xl">
          <div className="text-sm text-text-primary font-medium">
            ⚠ Hay configuraciones cargadas pero ninguna vigente fechaHoy
          </div>
          <p className="text-sm text-text-secondary mt-1">
            La fila más temprana tiene vigente_desde en el futuro. Si
            necesitás cobrar ya, agregá una con fecha de fechaHoy.
          </p>
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <Table>
          <THead>
            <Th>Vigente desde</Th>
            <Th align="right">Semestral</Th>
            <Th align="right">Mensual equivalente</Th>
            <Th>Acta</Th>
            <Th align="right">Acciones</Th>
          </THead>
          <TBody>
            {items.map((c) => (
              <Tr key={c.id} data-testid="fila-configuracion">
                <Td tone="primary">
                  {c.vigente_desde}
                  {vigente?.id === c.id && (
                    <span className="ml-2 text-xs bg-state-success/20 text-state-success px-1.5 py-0.5 rounded">
                      vigente
                    </span>
                  )}
                </Td>
                <Td align="right" tone="primary" className="font-medium">
                  {formatCLP(c.monto_semestral)}
                </Td>
                <Td align="right">{formatCLP(c.monto_mensual_equivalente)}</Td>
                <Td className="truncate max-w-xs">
                  {c.aprobado_acta_url ? (
                    <a
                      href={c.aprobado_acta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-admin-red hover:underline"
                    >
                      Ver acta
                    </a>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </Td>
                <Td align="right">
                  <ActionButton variant="edit" onClick={() => setEditando(c)}>
                    Editar
                  </ActionButton>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-4 max-w-3xl text-sm text-text-secondary">
        <strong className="text-text-primary">Doctrina append-only:</strong>{' '}
        para cambiar el monto, crear una <em>nueva</em> configuración con
        vigente_desde posterior. La edición solo permite corregir el URL del
        acta (typos), no tocar montos ni fechas vigentes.
      </div>

      {mostrarCrear && (
        <ModalCrear
          onCerrar={() => setMostrarCrear(false)}
          onCreada={() => {
            setMostrarCrear(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalEditar
          configuracion={editando}
          onCerrar={() => setEditando(null)}
          onActualizada={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
